import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { fileToBase64, getImageData, getImageFromFile } from "utils/logicWithFiles"
import { compressImageWithLoading } from "utils/imageCompression"
import { COMPRESSION_CONFIG } from "../../config/compressionConfig"
import {
  Box,
  Button,
  Card,
  CardAsset,
  CardBadge,
  CardBody,
  CardContent,
  CardHeader,
  CardSubtitle,
  CardTitle,
  IconButton,
  Typography,
  Loader,
} from "@strapi/design-system"
import { Plus, Trash } from "@strapi/icons"

interface UploadImageCardProps {
  onChange: (data: any, fileBase64?: string) => void
  src: string
  title: string
  required?: boolean
  widthToHeightRatio: number // Corrected prop name
}

const allowedFileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"]

export const UploadImageCard = ({
  onChange,
  src,
  title,
  required,
  widthToHeightRatio,
}: UploadImageCardProps) => {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [hover, setHover] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)

  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const onCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!allowedFileTypes.includes(file.type))
      return setError(t("uploadImageCard.invalidFileType") + ": PNG, JPEG, JPG, WEBP.")

    setIsCompressing(true)
    setCompressionProgress(0)
    setError(null)

    try {
      const result = await compressImageWithLoading(
        file,
        (progress) => setCompressionProgress(progress),
        COMPRESSION_CONFIG
      )

      const compressedFile = result.compressedFile
      const fileBase64 = await fileToBase64(compressedFile)

      getImageFromFile(compressedFile, ({ src, width, height }) => {
        const imageRatio = width / height

        if (Math.abs(imageRatio - widthToHeightRatio) > 0.01) {
          setError(t("uploadImageCard.invalidRatio", { widthToHeightRatio }))
          return
        }

        setData({
          name: compressedFile.name,
          type: compressedFile.type.split("/")[1].toUpperCase(),
          width,
          height,
          src,
        })
        onChange(compressedFile, fileBase64)
        setError(null)
      })
    } catch (error) {
      console.error("Compression failed:", error)
      const fileBase64 = await fileToBase64(file)

      getImageFromFile(file, ({ src, width, height }) => {
        const imageRatio = width / height

        if (Math.abs(imageRatio - widthToHeightRatio) > 0.01) {
          setError(t("uploadImageCard.invalidRatio", { widthToHeightRatio }))
          return
        }

        setData({
          name: file.name,
          type: file.type.split("/")[1].toUpperCase(),
          width,
          height,
          src,
        })
        onChange(file, fileBase64)
        setError(null)
      })
    } finally {
      setIsCompressing(false)
      setCompressionProgress(0)
      e.target.value = ""
    }
  }

  const handleRemoveImage = () => {
    setData(null)
    onChange(null, "")
    setError(null) // Clear any errors when removing the image
  }

  useEffect(() => {
    getImageData(src, (imageData) => {
      const fileName = src.split("/").slice(-1)[0]

      setData((prevData) => ({
        ...prevData,
        ...imageData,
        name: fileName,
        type: fileName.split(".")[1]?.toUpperCase() || "",
      }))
    })
  }, [src])

  return (
    <Box
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      style={{
        position: "relative",
        width: 240,
        height: 220,
      }}
    >
      <Typography variant="omega" bold>
        {title}
      </Typography>
      <Box style={{ width: 140, marginTop: 8 }}>
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: (!src && !data) || hover ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            gap: 8, // Add gap between buttons
            zIndex: "1",
          }}
        >
          <Button
            startIcon={isCompressing ? <Loader small /> : <Plus />}
            onClick={() => {
              if (!isCompressing) hiddenFileInput.current?.click()
            }}
            disabled={isCompressing}
          >
            <input
              value=""
              id={title.toLowerCase()}
              ref={hiddenFileInput}
              accept={allowedFileTypes.join(", ")}
              onChange={onCapture}
              type="file"
              hidden
              multiple={false}
              disabled={isCompressing}
            />
            {isCompressing
              ? `${t("uploadImageCard.compressing")} ${compressionProgress}%`
              : t("uploadImageCard.uploadImage")}
          </Button>
          {data && (
            <IconButton variant="danger" onClick={handleRemoveImage}>
              <Trash />
            </IconButton>
          )}
        </Box>
      </Box>
      <Card style={{ width: 240, marginTop: 8 }}>
        <CardHeader>
          <CardAsset src={data?.src || src} />
        </CardHeader>
        {data && (
          <CardBody>
            <CardContent>
              <CardTitle>{data.name}</CardTitle>
              <CardSubtitle>{`${data.width}✕${data.height}`}</CardSubtitle>
            </CardContent>
            <CardBadge>{data.type}</CardBadge>
          </CardBody>
        )}
      </Card>
      {error && (
        <Box className="!pt-2">
          <Typography variant="omega" textColor="danger600">
            {error}
          </Typography>
        </Box>
      )}
      {required && (
        <Typography variant="omega" textColor="danger600">
          {`* ${t("uploadImageCard.required")}`}
        </Typography>
      )}
    </Box>
  )
}
